import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AssistantDashboard from "../assistant-dashboard/AssistantDashboard";
import AssistantsList from "../assistants/AssistantsList";
import DashboardLayout from "../dashboard/layout/DashboardLayout";
import { Plus } from "lucide-react";
import { Assistant } from "../assistants/AssistantsList";

export default function AssistantsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const pathAssistantId = (params["*"] ?? "").split("/")[0] || undefined;
  const [filteredAssistants, setFilteredAssistants] = useState<Assistant[]>([]);
  const [showTypeSelectionDialog, setShowTypeSelectionDialog] = useState(false);
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    // Check for path parameter first
    if (pathAssistantId) {
      setSelectedAssistantId(pathAssistantId);
    } else {
      // Fall back to query parameter for backwards compatibility
      const searchParams = new URLSearchParams(location.search);
      const assistantId = searchParams.get("id");
      if (assistantId) {
        setSelectedAssistantId(assistantId);
        searchParams.delete("id");
        const qs = searchParams.toString();
        navigate(`/assistants/${assistantId}${qs ? `?${qs}` : ""}`, { replace: true });
      }
    }
  }, [location, pathAssistantId, navigate]);

  const handleAssistantSelect = (id: string) => {
    setSelectedAssistantId(id);
    const search = location.search ? location.search : "";
    navigate(`/assistants/${id}${search}`, { replace: true });
  };
  
  const handleCreateAssistant = () => {
    setShowTypeSelectionDialog(true);
  };

  const handleAssistantDeleted = (id: string) => {
    setFilteredAssistants(filteredAssistants.filter((assistant) => assistant.id !== id));
    setSelectedAssistantId(null);
  };

  const handleAssistantUpdated = (id: string, updates: { name?: string; status?: string }) => {
    setFilteredAssistants((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...updates } : a))
    );
  };

  const cancelAssistantSelection = (query?: string) => {
    setSelectedAssistantId(null);
    window.history.replaceState({}, "", `/assistants${query ? `${query}` : ""}`);
  };
  
  // Define action buttons
  const actionButtons = [
    {
      label: "Create Assistant",
      onClick: handleCreateAssistant,
      icon: <Plus size={16} />,
      variant: "default" as const,
      className: "bg-primary hover:bg-primary/80 text-primary-foreground"
    }
  ];

  return (
    <DashboardLayout 
      activeItem="Assistants"
      title="Assistants"
      description="Manage your voice AI assistants"
      actionButtons={actionButtons}
    >
      <div className="flex flex-1 min-h-0 overflow-hidden h-[calc(100vh-8rem)]">
        <div className="w-96 border-r border-border flex flex-col min-h-0 overflow-hidden shrink-0">
          <AssistantsList
            onAssistantSelect={handleAssistantSelect}
            selectedAssistantId={selectedAssistantId}
            className={null}
            filteredAssistants={filteredAssistants}
            setFilteredAssistants={setFilteredAssistants}
            showTypeSelectionDialog={showTypeSelectionDialog}
            setShowTypeSelectionDialog={setShowTypeSelectionDialog}
          />
        </div>
        <div className="flex-1 p-4 overflow-auto min-h-0">
          {selectedAssistantId ? (
            <AssistantDashboard assistantId={selectedAssistantId} onAssistantDeleted={handleAssistantDeleted} onCancelAssistantSelection={cancelAssistantSelection} onAssistantUpdated={handleAssistantUpdated} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground">
                Select an assistant to edit or create a new one
              </p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
